import Input from '@/components/forms/Input'
import Button from '@/components/misc/Button'
import { SubmitHandler, useForm } from 'react-hook-form'
import Select from '@/components/forms/Select'
import TextArea from '@/components/forms/TextArea'
import { vehicleTypeOptions } from '../filters/constants/statusOptions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createNewVehicle, editNewVehicle } from '@/api/vehiclesAPI'
import { GET_ALL_VEHICLES_QUERY_KEY } from '../hooks/useServerLogisticVehicle'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { User, Vehicle, VehicleType } from '@/interfaces/vehicle'
import { getProducts } from '@/api/productsAPI'
import { Form } from '@/components/ui/form'
import { SelectFormUsers } from './SelectFormUsers'
import { getUsers } from '@/api/usersAPI'
import useLogisticVehicleForm from '@/state/useLogisticVehicleForm'

interface IProp {
  close: () => void
  vehicleToEdit?: Vehicle
}
export default function NewVehicleModal({ close, vehicleToEdit }: IProp) {
  const form = useForm()
  const { control, handleSubmit, setError, watch } = form
  const queryClient = useQueryClient()
  const [isSending, setIsSending] = useState(false)
  const { data: containerUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      getUsers({
        page: 1,
        per_page: 10,
        search: '',
      }),
  })
  const vehicleName = useLogisticVehicleForm((state) => state.vehicle_name)
  const vehicleIdentification = useLogisticVehicleForm(
    (state) => state.vehicle_identification
  )
  const vehicleNumber = useLogisticVehicleForm((state) => state.vehicle_number)
  const vehicleType = useLogisticVehicleForm((state) => state.vehicle_type)
  const vehicleMaxWeight = useLogisticVehicleForm(
    (state) => state.vehicle_max_weight
  )
  const vehicleMaxHeight = useLogisticVehicleForm(
    (state) => state.vehicle_max_height
  )
  const vehicleUsers = useLogisticVehicleForm((state) => state.vehicle_users)
  const updateVehicleName = useLogisticVehicleForm(
    (state) => state.updateVehicleName
  )
  const updateVehicleIdentification = useLogisticVehicleForm(
    (state) => state.updateVehicleIdentification
  )
  const updateVehicleNumber = useLogisticVehicleForm(
    (state) => state.updateVehicleNumber
  )
  const updateVehicleType = useLogisticVehicleForm(
    (state) => state.updateVehicleType
  )
  const updateVehicleMaxWeight = useLogisticVehicleForm(
    (state) => state.updateVehicleMaxWeight
  )
  const updateVehicleMaxHeight = useLogisticVehicleForm(
    (state) => state.updateVehicleMaxHeight
  )
  const updateVehicleUsers = useLogisticVehicleForm(
    (state) => state.updateVehicleUsers
  )

  watch('vehicle_name') && updateVehicleName(watch('vehicle_name'))
  watch('vehicle_identification') &&
    updateVehicleIdentification(watch('vehicle_identification'))
  watch('vehicle_number') && updateVehicleNumber(watch('vehicle_number'))
  watch('vehicle_type') && updateVehicleType(watch('vehicle_type'))
  watch('vehicle_max_weight') &&
    updateVehicleMaxWeight(watch('vehicle_max_weight'))
  watch('vehicle_max_height') &&
    updateVehicleMaxHeight(watch('vehicle_max_height'))
  watch('vehicle_users') && updateVehicleUsers(watch('vehicle_users'))

  console.log(watch('vehicle_users'))

  const createVehicle = useMutation({
    mutationFn: createNewVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_ALL_VEHICLES_QUERY_KEY],
      })
      setIsSending(false)
      close()
      toast.success('El vehículo ha sido creado con éxito.')
    },
  })

  const editVehicle = useMutation({
    mutationFn: editNewVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_ALL_VEHICLES_QUERY_KEY],
      })
      setIsSending(false)
      close()
      toast.success('El vehículo ha sido editado con éxito.')
    },
    onError: (response: any) => {
      setIsSending(false)
      close()
      toast.error(
        JSON.stringify(
          response.response.statusText ||
            'Lo siento, ha ocurrido un error intentando editar'
        )
      )
    },
  })

  const onSubmit: SubmitHandler<
    Record<string, string | number | boolean | string[] | User>
  > = (data) => {
    if (!data.vehicle_name) {
      setError(
        'vehicle_name',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_identification) {
      setError(
        'vehicle_identification',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_number) {
      setError(
        'vehicle_number',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_type) {
      setError(
        'vehicle_type',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_max_weight) {
      setError(
        'vehicle_max_weight',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_max_height) {
      setError(
        'vehicle_max_height',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    if (!data.vehicle_users) {
      setError(
        'vehicle_users',
        {
          type: 'manual',
          message: '¡Este campo es requerido!',
        },
        { shouldFocus: true }
      )
      return
    }
    setIsSending(true)
    if (vehicleToEdit) {
      editVehicle.mutate({
        id: vehicleToEdit.id,
        name: data.vehicle_name as string,
        identification: data.vehicle_identification as string,
        oficialNumber: data.vehicle_number as string,
        type: data.vehicle_type as VehicleType,
        maxWeight: data.vehicle_max_weight as number,
        maxHeight: data.vehicle_max_height as number,
        operatedById: (data.vehicle_users as User)?.id as number,
      })
    } else {
      createVehicle.mutate({
        name: data.vehicle_name as string,
        identification: data.vehicle_identification as string,
        oficialNumber: data.vehicle_number as string,
        type: data.vehicle_type as VehicleType,
        maxWeight: data.vehicle_max_weight as number,
        maxHeight: data.vehicle_max_height as number,
        operatedById: (data.vehicle_users as User)?.id as number,
      })
    }
  }

  return (
    <div
      className={
        'flex h-full flex-col space-y-4 overflow-y-scroll px-2 scrollbar-thin'
      }
    >
      <Form {...form}>
        <h1 className={'mb-5 text-xl font-bold text-slate-700'}>
          {vehicleToEdit ? 'Editar vehículo' : 'Crear vehículo'}
        </h1>
        <div className="pb-2">
          <Input
            name={'vehicle_name'}
            control={control}
            label={'Nombre (*)'}
            placeholder={'Nombre...'}
            defaultValue={vehicleToEdit ? vehicleToEdit?.name : vehicleName}
          />
        </div>
        <div className="flex justify-between pb-2">
          <div className="w-64">
            <Input
              name={'vehicle_identification'}
              control={control}
              label={'Identificación (*)'}
              placeholder={'Identificación...'}
              defaultValue={
                vehicleToEdit
                  ? vehicleToEdit?.identification
                  : vehicleIdentification
              }
            />
          </div>
          <div className="ml-2 w-64">
            <Input
              name={'vehicle_number'}
              control={control}
              label={'Número oficial (*)'}
              placeholder={'Número oficial...'}
              defaultValue={
                vehicleToEdit ? vehicleToEdit?.oficialNumber : vehicleNumber
              }
            />
          </div>
        </div>
        <div className="pb-2">
          <Select
            label={'Tipo (*)'}
            name={'vehicle_type'}
            data={vehicleTypeOptions
              .map((status) => ({
                id: status.value,
                name: status.label,
              }))
              .filter((item) => item.name !== 'Todos')}
            control={control}
            placeholder={'Seleccione...'}
            defaultValue={vehicleToEdit ? vehicleToEdit?.type : vehicleType}
          />
        </div>
        <div className="flex justify-between pb-2">
          <div className="w-64">
            <Input
              name={'vehicle_max_weight'}
              control={control}
              type="number"
              label={'Peso máximo (*)'}
              placeholder={'Peso máximo...'}
              defaultValue={
                vehicleToEdit ? vehicleToEdit?.maxWeight : vehicleMaxWeight
              }
            />
          </div>
          <div className="ml-2 w-64">
            <Input
              name={'vehicle_max_height'}
              control={control}
              type="number"
              label={'Altura máxima (*)'}
              placeholder={'Altura máxima...'}
              defaultValue={
                vehicleToEdit ? vehicleToEdit?.maxHeight : vehicleMaxHeight
              }
            />
          </div>
        </div>
        <div className="pb-2">
          <SelectFormUsers
            label={'Operado por'}
            name={'vehicle_users'}
            data={
              containerUsers?.map(({ id, displayName, email }) => ({
                id: id,
                title: `${displayName}`,
                subtitle: email,
              })) ?? []
            }
            control={control}
            defaultValue={
              vehicleToEdit ? vehicleToEdit?.operatedBy : vehicleUsers
            }
          />
        </div>
        <div className={'mt-10 flex w-full justify-end'}>
          <Button
            name={'Guardar'}
            action={handleSubmit(onSubmit)}
            loading={isSending}
            disabled={isSending}
          />
        </div>
      </Form>
    </div>
  )
}
